/**
 * ReviewQueueButtonContainer — the pipeline toolbar entry point that opens the
 * Review-queue drawer (SE-5499).
 *
 * Self-contained: it drives the drawer reactive var directly, so it can be
 * dropped into any toolbar without prop-drilling an open callback. Scopes the
 * queue to the current board when a `kanbanBoardId` is supplied.
 * @remarks Container component — owns the open action.
 */

import { useCallback } from "react";

import { Button } from "@/components/atoms/Button";

import { useReviewQueueDrawerState } from "../../../hooks/useReviewQueueDrawerState";

/** Props for {@link ReviewQueueButtonContainer}. */
export interface ReviewQueueButtonContainerProps {
  /** Board scope for the queue; omit for the whole-org queue. */
  readonly kanbanBoardId?: string | null;
}

/**
 * Toolbar button that opens the Review-queue drawer.
 * @param props - {@link ReviewQueueButtonContainerProps}
 * @param props.kanbanBoardId - Board scope for the queue; omit for whole-org
 * @returns The button.
 */
const ReviewQueueButtonContainer = ({
  kanbanBoardId,
}: ReviewQueueButtonContainerProps) => {
  const { open } = useReviewQueueDrawerState();

  const handlePress = useCallback(
    () => open(kanbanBoardId ?? null),
    [open, kanbanBoardId]
  );

  return (
    <Button
      // SE-6488: the design frame (node 80:15021) labels this control "Review".
      // The accessibility label keeps the fuller "Open review queue" phrasing so
      // the shortened visible label does not cost screen-reader clarity.
      label="Review"
      variant="outline"
      tone="secondary"
      size="sm"
      labelTone="primary"
      /* SE-6865: the toolbar CTAs (Review / Activity / Rules / Add player) are
         given their radius and their label weight HERE, at the call site, and
         the two atom-level defects underneath are deliberately left standing.
         This is the anchor note; the other three sites point back at it.
         Raniel's scope call — do not "helpfully" widen it.

         RADIUS. The atom emits a radius class only when `shape` is passed, and
         it has no default. The fallback is the vendored Gluestack base's bare
         `rounded`, which compiles to NOTHING here: tailwind.config.js REPLACES
         the borderRadius scale (rather than extending it) with
         none/sm/md/lg/xl/full and no DEFAULT key. So every Button in the app
         that omits `shape` renders square corners — far wider than this ticket,
         and fixing it in the atom would silently round every square button in
         the app, which needs its own visual sweep first.

         WEIGHT. The vendored base sets a 600-weight class on the label, and
         `labelVariant` is the sanctioned typographic override. `body-strong` is
         the ONLY closed variant that actually displaces it: it resolves to
         `font-medium`, whose class NAME lands in tailwind-merge's font-weight
         group, so the merge drops the base's 600. A family-only variant such as
         `body` sits in the font-family group instead, does not conflict, and
         would leave the label asking the Satoshi family for 600 — which
         SE-5780 measured resolving UP to the real Bold face (see global.css).
         So `body` would have read heavier, not lighter. `body-strong` keeps the
         same 14px `size="sm"` already renders and lands on real Satoshi Medium.
         A true 400 Regular label is not reachable from a call site at all: it
         needs a label-weight axis on the atom — the same library gap
         PipelineChipsRow's SE-6554 note records for its 12px-medium trigger. */
      shape="rounded"
      labelVariant="body-strong"
      /* SE-6554: grey toolbar border, not the atom's near-black outline —
         interim call-site waiver, same one as Filters in TopPanelView (see the
         full rationale there). border300/`outline-strong` is the border the
         whole toolbar family shares in the design; the Button atom has no
         border-tone or outline-hover axis, so this comes off when that atom
         ticket lands. */
      className="web:hover:bg-surface-subtle border-outline-strong"
      accessibilityLabel="Open review queue"
      testID="review-queue-open"
      onPress={handlePress}
    />
  );
};

ReviewQueueButtonContainer.displayName = "ReviewQueueButtonContainer";

export default ReviewQueueButtonContainer;
